import { ErrorBoundary } from '@components/error-boundary';
import { ErrorScreen } from '@components/error-screen';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconRestart } from '@components/icons/restart';
import { IconSupport } from '@components/icons/support';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIntercom } from '@hooks/use-intercom';
import { useScopedTranslate } from '@locale/translate';

interface TravellerJourneyErrorBoundaryProps {
  children: React.ReactNode;
}

export const TravellerJourneyErrorBoundary = ({ children }: TravellerJourneyErrorBoundaryProps) => {
  const { t } = useScopedTranslate(locale);
  const { showMessenger, isEnabled: isIntercomEnabled } = useIntercom();

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleBack = () => {
    window.history.back();
  };

  const actions = [
    {
      label: t('refresh'),
      onClick: handleRefresh,
      icon: <IconRestart />,
    },
    {
      label: t('goBack'),
      onClick: handleBack,
      icon: <IconArrowLeft />,
    },
    ...(isIntercomEnabled
      ? [
          {
            label: t('contactSupport'),
            onClick: showMessenger,
            icon: <IconSupport />,
          },
        ]
      : []),
  ];

  return (
    <ErrorBoundary
      fallback={
        <ErrorScreen
          heading={t('heading')}
          message={
            <Stack orientation="vertical" space={2}>
              <Text text={t('primaryMessage')} />
              <Text text={t('supportMessage')} />
            </Stack>
          }
          actions={actions}
          flipped
        />
      }
    >
      {children}
    </ErrorBoundary>
  );
};

const locale = {
  en: {
    heading: 'Unexpected error',
    primaryMessage:
      "We encountered an issue while displaying this traveller's journey. Our team has been automatically notified of this error.",
    supportMessage:
      "If this issue is urgent, please don't hesitate to contact our customer support team who can assist you immediately.",
    refresh: 'Refresh',
    goBack: 'Back',
    contactSupport: 'Contact support',
  },
};
